import { useState } from 'react'
import { Brand } from '../../../shared/components/Brand'

// Rebus landing/consent — parallels the cryptic consent (same voice + opt-in structure and
// privacy link), with rebus-specific copy and a small self-made example rebus ("mind over
// matter"), so the example is copyright-free rather than one of the dataset images.
export const RebusConsent = ({ onConsent }: { onConsent: () => void }) => {
  const [checked, setChecked] = useState(false)
  const privacyHref =
    typeof window !== 'undefined'
      ? window.location.pathname.replace(/\/[^/]*$/, '') + '/privacy.html'
      : 'privacy.html'

  return (
    <div className="flex flex-col items-center px-6 pt-10 pb-6">
      <div className="w-full max-w-lg text-center">
        <Brand game="rebus" />

        {/* ---- The game ---- */}
        <h1 className="mt-8 text-xl font-semibold text-gray-800">Say what the picture says</h1>
        <p className="mt-1 text-sm text-gray-500">
          A rebus spells out a word or phrase using pictures, letters and where they sit.
        </p>

        <div className="mt-6 rounded-xl border border-gray-200 bg-white px-6 py-7 shadow-sm">
          <div className="mx-auto w-max text-center leading-none">
            <div className="text-2xl font-extrabold tracking-widest text-gray-800">MIND</div>
            <div className="my-2 h-0.5 w-full rounded bg-gray-400"></div>
            <div className="text-2xl font-extrabold tracking-widest text-gray-800">MATTER</div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            answer: <span className="font-semibold">mind over matter</span>
          </div>
        </div>

        {/* ---- How nerdle labs games work ---- */}
        <h2 className="mt-10 text-base font-semibold text-gray-800">How to play nerdle labs games</h2>
        <p className="mt-2 text-gray-600 leading-relaxed">
          Every nerdle labs game is voice-activated — talk out loud as you work it out, so we can see how you
          think. Keep talking to reveal the picture, and say <span className="font-medium">“commit”</span>{' '}
          (or tap the button) when you’ve got it.
        </p>

        {/* ---- Consent ---- */}
        <p className="mt-4 text-xs text-gray-400">
          We record your voice as text only — your browser transcribes it; no audio is kept.
        </p>

        <label className="mt-5 flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 p-4 text-left hover:bg-gray-50">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 accent-[#820458]"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span className="text-sm text-gray-700">
            I confirm I am 18 or over and consent to my voice being recorded, transcribed to text, and the resulting transcripts being
            used for gaming and research purposes. I understand no audio is retained.{' '}
            <a
              href={privacyHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="underline hover:text-[#820458]"
            >
              More details.
            </a>
          </span>
        </label>

        <button
          type="button"
          disabled={!checked}
          onClick={onConsent}
          className="mt-5 w-full rounded-lg bg-[#820458] py-3 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Start
        </button>
        <p className="mt-2 text-xs text-gray-400">Your browser will ask for microphone access next.</p>
        <p className="mt-3 text-xs text-gray-400">
          <a href={privacyHref} className="underline hover:text-[#820458]">
            Privacy policy
          </a>
        </p>
      </div>
    </div>
  )
}
